import { useAuth } from "@/contexts/AuthContext";
import { cn } from "@/lib/utils";
import {
  ChevronLeft,
  ClipboardList,
  FileText,
  Home,
  Key,
  Mail,
  Settings,
  X,
} from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "./ui/button";

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
}

interface NavItem {
  name: string;
  path: string;
  icon: React.ComponentType<{ className?: string }>;
  adminOnly?: boolean;
}

const navigationItems: NavItem[] = [
  { name: "Dashboard", path: "/dashboard", icon: Home },
  { name: "Reports", path: "/reports", icon: FileText },
  { name: "Templates", path: "/email-templates", icon: Mail },
  { name: "API Tokens", path: "/api-tokens", icon: Key },
  { name: "Admin", path: "/admin", icon: Settings, adminOnly: true },
];

export function Sidebar({
  isOpen,
  onClose,
  isCollapsed = false,
  onToggleCollapse,
}: SidebarProps) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const filteredNavItems = navigationItems.filter(
    (item) => !item.adminOnly || user?.role === "admin",
  );

  const handleNavigation = (path: string) => {
    navigate(path);
    onClose();
  };

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          "fixed top-0 left-0 z-50 h-full bg-sidebar border-r border-sidebar-border",
          "flex flex-col transition-all duration-300 ease-out-expo",
          // Mobile: slide in/out
          "lg:translate-x-0",
          isOpen ? "translate-x-0" : "-translate-x-full",
          // Desktop: collapsed state
          isCollapsed ? "lg:w-20" : "lg:w-64",
          "w-64",
        )}
      >
        {/* Header */}
        <div className="flex items-center justify-between h-16 px-4 border-b border-sidebar-border">
          <div
            className={cn(
              "flex items-center gap-3 transition-opacity duration-200",
              isCollapsed && "lg:opacity-0 lg:w-0 lg:overflow-hidden",
            )}
          >
            <div className="flex items-center justify-center w-9 h-9 rounded-lg bg-gradient-to-br from-purple-500 to-pink-500">
              <ClipboardList className="w-5 h-5 text-white" />
            </div>
            <span className="font-semibold text-foreground">Weekly Report</span>
          </div>

          {/* Mobile close button */}
          <Button
            variant="ghost"
            size="icon-sm"
            className="lg:hidden"
            onClick={onClose}
          >
            <X className="w-5 h-5" />
          </Button>

          {/* Desktop collapse button */}
          {onToggleCollapse && (
            <Button
              variant="ghost"
              size="icon-sm"
              className="hidden lg:flex"
              onClick={onToggleCollapse}
            >
              <ChevronLeft
                className={cn(
                  "w-5 h-5 transition-transform duration-200",
                  isCollapsed && "rotate-180",
                )}
              />
            </Button>
          )}
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto scrollbar-thin">
          {filteredNavItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;

            return (
              <button
                key={item.path}
                onClick={() => handleNavigation(item.path)}
                className={cn(
                  "nav-item w-full",
                  isActive && "active",
                  isCollapsed && "lg:justify-center lg:px-0",
                )}
                title={isCollapsed ? item.name : undefined}
              >
                <Icon
                  className={cn(
                    "w-5 h-5 flex-shrink-0",
                    isActive ? "text-primary" : "text-muted-foreground",
                  )}
                />
                <span
                  className={cn(
                    "transition-opacity duration-200",
                    isCollapsed && "lg:hidden",
                  )}
                >
                  {item.name}
                </span>
              </button>
            );
          })}
        </nav>

        {/* Footer with user info */}
        <div
          className={cn(
            "p-4 border-t border-sidebar-border",
            isCollapsed && "lg:p-2",
          )}
        >
          <div
            className={cn(
              "flex items-center gap-3",
              isCollapsed && "lg:justify-center",
            )}
          >
            <div className="flex items-center justify-center w-9 h-9 rounded-full bg-gradient-to-br from-purple-500/20 to-pink-500/20 text-purple-400 text-sm font-medium">
              {user?.name?.charAt(0) || user?.email?.charAt(0) || "?"}
            </div>
            <div
              className={cn(
                "flex-1 min-w-0 transition-opacity duration-200",
                isCollapsed && "lg:hidden",
              )}
            >
              <p className="text-sm font-medium text-foreground truncate">
                {user?.name || user?.email}
              </p>
              <p className="text-xs text-muted-foreground capitalize">
                {user?.role || "User"}
              </p>
            </div>
          </div>
        </div>
      </aside>
    </>
  );
}

export default Sidebar;
